import { createFileRoute, useNavigate, Link } from '@tanstack/react-router';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { usePlatformAdmin } from '../../../hooks';
import {
  MACHINE_SPECS,
  REGION_PRICING,
  REGION_LABELS,
  computePrice,
  relativePerf,
  HOURS_PER_MONTH,
  type MachineSpec,
  type MachineFamily,
  type PriceBreakdown,
} from '../../../lib/gcpPricing';
import adminStyles from '../Admin.module.css';
import styles from './Pricing.module.css';

export const Route = createFileRoute('/admin/pools/pricing')({
  component: PoolPricingRoute,
});

type ViewRegion = string;

const FAMILY_LABEL: Record<MachineFamily, string> = {
  g2: 'G2 — NVIDIA L4 (Ada)',
  g4: 'G4 — NVIDIA RTX PRO 6000 (Blackwell)',
  n1: 'N1 + T4 vWS (Turing)',
};

const GPU_DESC: Record<string, string> = {
  l4: 'L4',
  'rtx-pro-6000': 'RTX-Pro 6000',
  't4-vws': 'T4 vWS',
};

const FAMILY_REGION_GROUPS: Record<MachineFamily, ViewRegion[]> = {
  g2: ['us-central1', 'us-west1', 'us-east4', 'europe-west2', 'europe-west4', 'europe-west6', 'asia-northeast1'],
  g4: ['us-central1', 'us-west1', 'us-east1', 'us-east4', 'us-south1', 'us-east5', 'us-west3', 'europe-west1', 'europe-west2', 'europe-west4', 'europe-west8', 'europe-north1', 'asia-southeast1'],
  n1: ['us-central1', 'us-west1', 'us-east1', 'australia-southeast1'],
};

function fmtUsd(n: number, digits = 3): string {
  return `$${n.toFixed(digits)}`;
}

function fmtPerf(n: number): string {
  return n.toFixed(1) + '×';
}

function PoolPricingRoute() {
  const { isPlatformAdmin } = usePlatformAdmin();
  const navigate = useNavigate();

  const [withWindows, setWithWindows] = useState(true);
  const [bootDiskGb, setBootDiskGb] = useState(100);
  const [rankRegion, setRankRegion] = useState<ViewRegion>('us-central1');

  useEffect(() => {
    if (isPlatformAdmin === false) navigate({ to: '/' });
  }, [isPlatformAdmin, navigate]);

  // Rank all shapes priceable in the selected region by perf-per-dollar
  // (perf relative to L4 baseline / $/hr at current Windows + boot-disk
  // inputs). Higher = better value.
  const ranked = useMemo(() => {
    const rows: Array<{
      spec: MachineSpec;
      price: PriceBreakdown;
      perf: number;
      perfPerDollar: number;
    }> = [];
    for (const spec of MACHINE_SPECS) {
      const price = computePrice(spec, rankRegion, { withWindows, bootDiskGb });
      if (!price) continue;
      const perf = relativePerf(spec);
      rows.push({ spec, price, perf, perfPerDollar: perf / price.total });
    }
    rows.sort((a, b) => b.perfPerDollar - a.perfPerDollar);
    return rows;
  }, [rankRegion, withWindows, bootDiskGb]);

  if (!isPlatformAdmin) return null;

  return (
    <div className={adminStyles.container}>
      <header className={adminStyles.header}>
        <div>
          <h1 className={adminStyles.title}>Pool Pricing Reference</h1>
          <div className={styles.subtitle}>
            <span>GCP on-demand list prices</span>
            <Link to="/admin/pools" className={styles.crumbLink}>Back to Pools</Link>
          </div>
        </div>
      </header>

      <section className={styles.controlsBar}>
        <label className={styles.control}>
          <span className={styles.controlLabel}>OS</span>
          <select
            className={styles.controlInput}
            value={withWindows ? 'win' : 'lin'}
            onChange={e => setWithWindows(e.target.value === 'win')}
          >
            <option value="win">Windows Server 2025</option>
            <option value="lin">Linux</option>
          </select>
        </label>
        <label className={styles.control}>
          <span className={styles.controlLabel}>Boot disk (pd-ssd)</span>
          <select
            className={styles.controlInput}
            value={bootDiskGb}
            onChange={e => setBootDiskGb(parseInt(e.target.value, 10))}
          >
            <option value={50}>50 GB</option>
            <option value={100}>100 GB</option>
            <option value={200}>200 GB</option>
            <option value={500}>500 GB</option>
          </select>
        </label>
        <label className={styles.control}>
          <span className={styles.controlLabel}>Rank region</span>
          <select
            className={styles.controlInput}
            value={rankRegion}
            onChange={e => setRankRegion(e.target.value)}
          >
            {Object.keys(REGION_PRICING).map(r => (
              <option key={r} value={r}>{REGION_LABELS[r] ?? r} · {r}</option>
            ))}
          </select>
        </label>
        <div className={styles.controlNote}>
          List prices · SUDs not applied · pulled from Cloud Billing 2026-05-15
        </div>
      </section>

      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>
          Best perf/$ — {withWindows ? 'Windows' : 'Linux'} single-session
        </h2>
        <p className={styles.sectionLead}>
          Shapes ranked by relative graphics-and-encode perf (L4 = 1.0×)
          per hourly dollar in <strong>{REGION_LABELS[rankRegion] ?? rankRegion}</strong>.
          Top of the list is the cheapest way to get a given amount of GPU work done.
        </p>

        <div className={styles.tableScroll}>
          <table className={styles.priceTable}>
            <thead>
              <tr>
                <th className={styles.colRank}>#</th>
                <th className={styles.colShape}>Shape</th>
                <th>GPU</th>
                <th className={styles.numCol}>vCPU</th>
                <th className={styles.numCol}>RAM</th>
                <th className={styles.numCol}>VRAM</th>
                <th className={styles.numCol}>$/hr</th>
                <th className={styles.numCol}>$/mo</th>
                <th className={styles.numCol}>Rel. perf</th>
                <th className={styles.numCol}>perf/$ (rel/hr)</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((row, i) => {
                const isWinner = i === 0;
                const gpuDesc = `${row.spec.gpu.count}× ${GPU_DESC[row.spec.gpu.kind]}` +
                  (row.spec.gpu.fraction ? ` (1/${Math.round(1 / row.spec.gpu.fraction)} slice)` : '');
                return (
                  <tr key={row.spec.name} className={isWinner ? styles.winnerRow : undefined}>
                    <td className={`${styles.colRank} ${isWinner ? styles.winnerRank : ''}`}>
                      {i + 1}
                    </td>
                    <td className={styles.colShape}>
                      <span className={styles.shapeName}>{row.spec.name}</span>
                    </td>
                    <td>{gpuDesc}</td>
                    <td className={styles.numCol}>{row.spec.vcpus}</td>
                    <td className={styles.numCol}>{row.spec.ramGb} GB</td>
                    <td className={styles.numCol}>{row.spec.gpu.vramGb} GB</td>
                    <td className={styles.numCol}>{fmtUsd(row.price.total)}</td>
                    <td className={styles.numCol}>
                      ${(row.price.total * HOURS_PER_MONTH).toFixed(0)}
                    </td>
                    <td className={styles.numCol}>{fmtPerf(row.perf)}</td>
                    <td className={`${styles.numCol} ${styles.strong}`}>
                      {row.perfPerDollar.toFixed(2)}
                    </td>
                  </tr>
                );
              })}
              {ranked.length === 0 && (
                <tr>
                  <td colSpan={10} className={adminStyles.empty}>
                    No shapes priced in this region
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>

      <FamilyBreakdown
        family="g2"
        withWindows={withWindows}
        bootDiskGb={bootDiskGb}
      />
      <FamilyBreakdown
        family="g4"
        withWindows={withWindows}
        bootDiskGb={bootDiskGb}
      />
      <FamilyBreakdown
        family="n1"
        withWindows={withWindows}
        bootDiskGb={bootDiskGb}
      />

      <section className={styles.notes}>
        <h3 className={styles.notesTitle}>Notes &amp; caveats</h3>
        <ul className={styles.notesList}>
          <li>
            <strong>List prices only.</strong> Sustained-use discounts (SUD) auto-apply at
            billing — typically 10–30% off for instances running &gt; 25% of a month. Actual
            spend is lower than the figures here.
          </li>
          <li>
            <strong>Windows licensing</strong> is $0.046 / vCPU / hour on Server 2025 Datacenter.
            On a g2-standard-12 that's $0.55/hr — about a third of the bill.
          </li>
          <li>
            <strong>G4 MIG-slice billing.</strong> Fractional shapes (g4-standard-6/12/24)
            are assumed to be charged at the slice fraction of the per-GPU SKU. The Cloud
            Billing catalog doesn't expose a per-slice SKU, so verify against the GCP
            pricing calculator before committing.
          </li>
          <li>
            <strong>Local SSD</strong> on G2/N1 must be attached and is billed per-GB; on
            G4 it's bundled into the shape ($0 line item) at fixed sizes.
          </li>
          <li>
            <strong>Region coverage.</strong> A cell shows "—" when GCP doesn't price that
            shape in that region. Notable gaps: G4 not in europe-west6 (Zurich),
            asia-northeast1 (Tokyo), or australia-southeast1 (Sydney) at this snapshot.
          </li>
          <li>
            <strong>Relative perf</strong> uses L4 = 1.0× as the baseline, T4 ≈ 0.33×,
            RTX PRO 6000 (full) ≈ 3.5×. Coarse ratio for ranking only — measure your
            actual UE workload before sizing.
          </li>
        </ul>
      </section>
    </div>
  );
}

function FamilyBreakdown({
  family,
  withWindows,
  bootDiskGb,
}: {
  family: MachineFamily;
  withWindows: boolean;
  bootDiskGb: number;
}) {
  const specs = useMemo(() => MACHINE_SPECS.filter(s => s.family === family), [family]);
  const regions = FAMILY_REGION_GROUPS[family];
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleRow = (name: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>{FAMILY_LABEL[family]}</h2>
      <p className={styles.sectionLead}>
        Per-region $/hr · {withWindows ? 'Windows' : 'Linux'} · {bootDiskGb} GB boot pd-ssd
        {family !== 'g4' ? ' + 375 GB Local SSD' : ' (Local SSD bundled)'}.
        Click a row to see the line-item breakdown.
      </p>

      <div className={styles.tableScroll}>
        <table className={styles.priceTable}>
          <thead>
            <tr>
              <th className={styles.colShape}>Shape</th>
              <th>GPU</th>
              <th className={styles.numCol}>vCPU/RAM</th>
              <th className={styles.numCol}>VRAM</th>
              {regions.map(r => (
                <th key={r} className={styles.numCol} title={REGION_LABELS[r] ?? r}>
                  {r}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {specs.map(spec => {
              const isExpanded = expanded.has(spec.name);
              const cells = regions.map(r => ({
                region: r,
                price: computePrice(spec, r, { withWindows, bootDiskGb }),
              }));
              const gpuDesc = `${spec.gpu.count}× ${GPU_DESC[spec.gpu.kind]}` +
                (spec.gpu.fraction ? ` (1/${Math.round(1 / spec.gpu.fraction)})` : '');
              return (
                <Fragment key={spec.name}>
                  <tr
                    className={`${styles.clickableRow} ${isExpanded ? styles.expandedRow : ''}`}
                    onClick={() => toggleRow(spec.name)}
                  >
                    <td className={styles.colShape}>
                      <span className={styles.toggleCaret}>{isExpanded ? '▾' : '▸'}</span>
                      <span className={styles.shapeName}>{spec.name}</span>
                    </td>
                    <td>{gpuDesc}</td>
                    <td className={styles.numCol}>{spec.vcpus} / {spec.ramGb} GB</td>
                    <td className={styles.numCol}>{spec.gpu.vramGb} GB</td>
                    {cells.map(({ region, price }) => (
                      <td key={region} className={styles.numCol}>
                        {price ? fmtUsd(price.total) : <span className={styles.dim}>—</span>}
                      </td>
                    ))}
                  </tr>
                  {isExpanded && (
                    <tr className={styles.detailRow}>
                      <td colSpan={4 + regions.length}>
                        <BreakdownGrid
                          spec={spec}
                          regions={regions}
                          withWindows={withWindows}
                          bootDiskGb={bootDiskGb}
                        />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function BreakdownGrid({
  spec,
  regions,
  withWindows,
  bootDiskGb,
}: {
  spec: MachineSpec;
  regions: string[];
  withWindows: boolean;
  bootDiskGb: number;
}) {
  return (
    <div className={styles.breakdownGrid}>
      {regions.map(r => {
        const price = computePrice(spec, r, { withWindows, bootDiskGb });
        if (!price) {
          return (
            <div key={r} className={styles.breakdownCard}>
              <div className={styles.breakdownCardHeader}>
                {REGION_LABELS[r] ?? r} <span className={styles.dim}>· {r}</span>
              </div>
              <div className={styles.dim}>Not priced in this region</div>
            </div>
          );
        }
        const monthly = price.total * HOURS_PER_MONTH;
        return (
          <div key={r} className={styles.breakdownCard}>
            <div className={styles.breakdownCardHeader}>
              {REGION_LABELS[r] ?? r} <span className={styles.dim}>· {r}</span>
            </div>
            <dl className={styles.breakdownList}>
              <dt>Compute (vCPU + RAM)</dt>
              <dd>{fmtUsd(price.compute, 4)}</dd>
              <dt>GPU</dt>
              <dd>{fmtUsd(price.gpu, 4)}</dd>
              {price.vws > 0 && (
                <>
                  <dt>vWS license</dt>
                  <dd>{fmtUsd(price.vws, 4)}</dd>
                </>
              )}
              {price.windows > 0 && (
                <>
                  <dt>Windows license</dt>
                  <dd>{fmtUsd(price.windows, 4)}</dd>
                </>
              )}
              <dt>Disks (boot + local SSD)</dt>
              <dd>{fmtUsd(price.disks, 4)}</dd>
              <dt className={styles.totalDt}>Total / hr</dt>
              <dd className={styles.totalDd}>{fmtUsd(price.total)}</dd>
              <dt className={styles.totalDt}>Total / mo</dt>
              <dd className={styles.totalDd}>${monthly.toFixed(0)}</dd>
            </dl>
          </div>
        );
      })}
    </div>
  );
}
